// domain/constants/targetStatus.js

/**
 * Where a target sits in its own lifecycle.
 *
 * TWO AXES WEARING ONE NAME, and worth knowing before changing anything here. The seven values
 * conflate a teaching PHASE — probe, acquisition, mastered, advanced by delivered data — with a
 * LIFECYCLE STATE — waiting, hold, aborted, closed, decided by a clinician. `canCollectData` is
 * the exact discriminator, and it predates anyone naming the distinction: true for the three phase
 * values, false for the four lifecycle ones.
 *
 * That conflation is why the field has three writers (TargetCard, CollectData, runWorkflows) and
 * why the date rules in `targetLifecycle` read as fiddly: clearing a mastery date on `waiting` is a
 * lifecycle value resetting a phase artifact. Splitting the field is a schema change nobody has
 * asked for. Recording why it fights back is cheaper than rediscovering it.
 *
 * @domainObject value-object
 * @invariant Owned by Treatment Planning. Care Delivery advances a target through published
 *   commands rather than writing the field, because a target carries a status before any session
 *   exists — an imported plan would otherwise be written by a context that has delivered nothing.
 * @invariant A workflow may add custom statuses beyond these seven. Every custom status counts as
 *   `acquisition` for progress purposes; see `targetProgress`.
 * @typedef {'waiting'|'probe'|'acquisition'|'mastered'|'closed'|'hold'|'aborted'} TargetStatus
 */
export const TARGET_STATUS = {
  WAITING: 'waiting',
  PROBE: 'probe',
  ACQUISITION: 'acquisition',
  MASTERED: 'mastered',
  CLOSED: 'closed',
  HOLD: 'hold',
  ABORTED: 'aborted',
};

/**
 * Which of the two conflated axes a status belongs to.
 *
 * @domainObject value-object
 * @typedef {'phase'|'lifecycle'} TargetStatusAxis
 */
export const TARGET_STATUS_AXIS = {
  PHASE: 'phase',
  LIFECYCLE: 'lifecycle',
};

/**
 * What each status means for dates, data collection and ordering.
 *
 * `labelKey` rather than a resolved label: a context does not read i18n, and resolving at module
 * scope is what freezes `boot/globals` in English for every locale.
 *
 * NO ICON OR COLOUR HERE. They are presentation, they are the only part of the legacy shape this
 * context does not own, and `boot/globals` supplies them to its own readers.
 *
 * `order` leaves gaps of 10000 on purpose. A workflow's custom statuses are sorted into those gaps,
 * so consecutive integers would leave nowhere to put them.
 *
 * The `reset*` flags say which dates a transition CLEARS; `stampsDate` says which it SETS when
 * absent. Both are consumed only by `targetLifecycle.applyStatusChange`, which is the one place
 * allowed to read them.
 */
export const TARGET_STATUS_META = {
  [TARGET_STATUS.WAITING]: {
    value: TARGET_STATUS.WAITING,
    labelKey: 'targetStatus_waiting',
    axis: TARGET_STATUS_AXIS.LIFECYCLE,
    canCollectData: false,
    order: 0,
    resetsDates: ['introductionDate', 'masteryDate', 'holdDate', 'abortedDate'],
    stampsDate: null,
  },
  [TARGET_STATUS.PROBE]: {
    value: TARGET_STATUS.PROBE,
    labelKey: 'targetStatus_probe',
    axis: TARGET_STATUS_AXIS.PHASE,
    canCollectData: true,
    order: 10000,
    resetsDates: ['masteryDate', 'holdDate', 'abortedDate'],
    stampsDate: null,
  },
  [TARGET_STATUS.ACQUISITION]: {
    value: TARGET_STATUS.ACQUISITION,
    labelKey: 'targetStatus_acquisition',
    axis: TARGET_STATUS_AXIS.PHASE,
    canCollectData: true,
    order: 20000,
    resetsDates: ['masteryDate', 'holdDate', 'abortedDate'],
    stampsDate: null,
  },
  [TARGET_STATUS.MASTERED]: {
    value: TARGET_STATUS.MASTERED,
    labelKey: 'targetStatus_mastered',
    axis: TARGET_STATUS_AXIS.PHASE,
    canCollectData: true,
    order: 30000,
    resetsDates: ['holdDate', 'abortedDate'],
    stampsDate: 'masteryDate',
  },
  [TARGET_STATUS.CLOSED]: {
    value: TARGET_STATUS.CLOSED,
    labelKey: 'targetStatus_closed',
    axis: TARGET_STATUS_AXIS.LIFECYCLE,
    canCollectData: false,
    order: 100000,
    resetsDates: ['holdDate', 'abortedDate'],
    stampsDate: 'masteryDate',
  },
  [TARGET_STATUS.HOLD]: {
    value: TARGET_STATUS.HOLD,
    labelKey: 'targetStatus_hold',
    axis: TARGET_STATUS_AXIS.LIFECYCLE,
    canCollectData: false,
    order: 110000,
    resetsDates: ['abortedDate'],
    stampsDate: 'holdDate',
  },
  [TARGET_STATUS.ABORTED]: {
    value: TARGET_STATUS.ABORTED,
    labelKey: 'targetStatus_aborted',
    axis: TARGET_STATUS_AXIS.LIFECYCLE,
    canCollectData: false,
    order: 120000,
    resetsDates: ['holdDate'],
    stampsDate: 'abortedDate',
  },
};

/** Built-in statuses in progression order. A workflow's custom statuses are not included. */
export const TARGET_STATUS_ORDER = Object.values(TARGET_STATUS_META)
  .sort((a, b) => a.order - b.order)
  .map(({ value }) => value);

/**
 * @param {unknown} value
 * @returns {boolean} whether the value is one of the seven built-in statuses. A workflow's custom
 *   status is NOT one, which is deliberate: callers that need to accept custom statuses have to say
 *   so rather than getting them by accident.
 */
export function isTargetStatus(value) {
  return Object.prototype.hasOwnProperty.call(TARGET_STATUS_META, value);
} // isTargetStatus()

/**
 * @param {string} status
 * @returns {boolean} whether data can be collected against a target in this status. This is also
 *   the discriminator between the two axes — see the note on TARGET_STATUS.
 */
export function canCollectData(status) {
  return TARGET_STATUS_META[status]?.canCollectData === true;
} // canCollectData()
